import React from 'react';
import { Outlet, useLocation, matchPath } from 'react-router-dom';
import CircularProgress from 'apollo-react/components/CircularProgress';
import { ErrorPage } from '../../error/ErrorPage';
import { ROUTE_PERMISSIONS } from '../../../constants/routePermissions';
import {
  PRIVILEGE_ACTIONS,
  REQUEST_PRIVILEGES,
} from '../../../constants/privilege.constant';
import { RESOURCES } from '../../../constants/resourceConstants';
import usePermissions from '../../../hooks/features/permissions/usePermissions';
import '../RouteGuard/RouteGuard.css';

/**
 * Resolves the required RBAC resource for the current pathname
 * by matching against ROUTE_PERMISSIONS (single source of truth).
 * Returns null if no permission is required (route is open to all authenticated users).
 *
 * Supports two matching modes from ROUTE_PERMISSIONS:
 *   - Default (end omitted or true): exact path match only.
 *   - end: false: prefix match — all sub-routes share the same resource.
 */
const getRequiredResource = (pathname) => {
  const match = ROUTE_PERMISSIONS.find((entry) =>
    matchPath({ path: entry.path, end: entry.end !== false }, pathname)
  );
  return match?.resource || null;
};

/**
 * Route-level authorization middleware.
 * Wraps a route subtree once -- automatically resolves the required resource
 * from ROUTE_PERMISSIONS for the current pathname.
 *
 * - Routes listed in ROUTE_PERMISSIONS require the mapped privilege (or isSuper).
 * - Routes NOT listed are accessible to any authenticated user.
 * - Unauthorized access renders a 404 page (security: no resource disclosure).
 *
 * SPECIAL CASE: REQUESTS resource requires REVIEW privilege.
 * Super admin must also have this specific privilege (no bypass).
 *
 * IMPORTANT: This provides FRONTEND-ONLY authorization check.
 * Backend 403 responses are handled separately by axios interceptor.
 */
const AuthorizedRoute = () => {
  const { pathname } = useLocation();
  const {
    hasPrivilege,
    userContext: { isSuper },
    isLoading,
  } = usePermissions();

  if (isLoading) {
    return (
      <div className="route-guard-loading">
        <CircularProgress />
      </div>
    );
  }

  const requiredResource = getRequiredResource(pathname);

  // Frontend route-level authorization check
  if (requiredResource) {
    let hasAccess = false;

    if (requiredResource === RESOURCES.REQUESTS) {
      // Special case: REQUESTS resource requires REVIEW privilege
      // Super admin is explicitly excluded from requests functionality
      hasAccess = hasPrivilege(requiredResource, REQUEST_PRIVILEGES.REVIEW, {
        excludeSuper: true, // Super admin should never access request routes
      });
    } else {
      // Standard case: require READ privilege, super admin bypasses
      hasAccess =
        isSuper || hasPrivilege(requiredResource, PRIVILEGE_ACTIONS.READ);
    }

    if (!hasAccess) {
      return <ErrorPage statusCode={404} />;
    }
  }

  return <Outlet />;
};

export default AuthorizedRoute;
